// ExpiryDatePicker.jsx
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';
import {
  Wrapper,
  Trigger,
  ChevronDownIcon,
  Dropdown,
  YearNav,
  YearLabel,
  IconButton,
  Panel,
  MonthGrid,
  MonthButton,
} from './styles';

// Helper: interpret 2-digit year as 2000 + yy (expiry-style)
const normalizeYear = yy => {
  if (Number.isNaN(yy)) return null;
  if (yy >= 100) return yy;
  return 2000 + yy;
};

export default function ExpiryDatePicker({
  value,
  onChange,
  onBlur,
  className,
  error,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null); // 1-12
  const [selectedYear, setSelectedYear] = useState(null); // full year
  const dropdownRef = useRef(null);

  const now = new Date();
  const currentMonthIndex = now.getMonth(); // 0-11
  const currentYearValue = now.getFullYear();

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Parse existing value: expected "MM-YY" or "MM-YYYY"
  useEffect(() => {
    if (!value) {
      // Reset internal state when value is cleared
      setSelectedMonth(null);
      setSelectedYear(null);
      setCurrentYear(new Date().getFullYear());
      return;
    }
    const parts = value.split('-');
    if (parts.length === 2) {
      const m = parseInt(parts[0], 10);
      const yRaw = parseInt(parts[1], 10);
      const y = normalizeYear(yRaw);
      if (!Number.isNaN(m) && y) {
        setSelectedMonth(m);
        setSelectedYear(y);
        // Keep the view scrolled to parsed year
        setCurrentYear(y);
      }
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = evt => {
      if (dropdownRef.current && !dropdownRef.current.contains(evt.target)) {
        // Only trigger onBlur if dropdown was actually open
        if (isOpen) {
          setIsOpen(false);
          if (onBlur) {
            onBlur();
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur, isOpen]);

  const isMonthDisabled = (monthIdxZeroBased, year) => {
    if (year < currentYearValue) return true;
    if (year === currentYearValue && monthIdxZeroBased < currentMonthIndex)
      return true;
    return false;
  };

  const handleMonthYearSelect = (monthNum, year) => {
    setSelectedMonth(monthNum);
    setSelectedYear(year);
    onChange(String(monthNum).padStart(2, '0'), String(year));
    setIsOpen(false);
  };

  function fmtMMYY(m, y) {
    if (!m || !y) return '';
    const mm = String(m).padStart(2, '0');
    const yy = String(y).slice(-2);
    return `${mm}-${yy}`;
  }

  const displayValue = fmtMMYY(selectedMonth, selectedYear);

  return (
    <Wrapper ref={dropdownRef} className={className}>
      <Trigger
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(o => !o)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') setIsOpen(o => !o);
        }}
        $error={!!error}
      >
        <span className={displayValue ? 'value' : 'placeholder'}>
          {displayValue || 'MM-YY'}
        </span>
        <ChevronDownIcon $open={isOpen} aria-hidden>
          <ChevronDown size={16} />
        </ChevronDownIcon>
      </Trigger>

      {isOpen && (
        <Dropdown>
          {/* Year navigation */}
          <YearNav>
            <IconButton
              type="button"
              aria-label="Previous year"
              onClick={() => setCurrentYear(y => y - 1)}
              disabled={currentYear <= currentYearValue}
            >
              <ChevronLeft size={16} />
            </IconButton>

            <YearLabel>{currentYear}</YearLabel>

            <IconButton
              type="button"
              aria-label="Next year"
              onClick={() => setCurrentYear(y => y + 1)}
              disabled={currentYear >= currentYearValue + 20}
            >
              <ChevronRight size={16} />
            </IconButton>
          </YearNav>

          {/* Month grid */}
          <Panel>
            <MonthGrid>
              {months.map((month, idx) => {
                const monthNum = idx + 1; // 1-12
                const disabled = isMonthDisabled(idx, currentYear);
                const selected =
                  selectedMonth === monthNum && selectedYear === currentYear;

                return (
                  <MonthButton
                    key={month}
                    type="button"
                    onClick={() => handleMonthYearSelect(monthNum, currentYear)}
                    disabled={disabled}
                    $disabled={disabled}
                    $selected={selected}
                  >
                    {month.slice(0, 3)}
                  </MonthButton>
                );
              })}
            </MonthGrid>
          </Panel>
        </Dropdown>
      )}
    </Wrapper>
  );
}

ExpiryDatePicker.propTypes = {
  value: PropTypes.string, // "MM-YY" or "MM-YYYY"
  onChange: PropTypes.func.isRequired, // (month, year) => void
  onBlur: PropTypes.func,
  className: PropTypes.string,
  error: PropTypes.bool,
};
